import { useGridApiRef } from "@mui/x-data-grid-pro";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchLatestEmployeeBidChoiceEventsByEidAndDate } from "../../api/bid";
import { getBidTypeFeaturesByStation } from "../../api/commonConfig/bidConfig";
import { isUserAuthorizedByServer } from "../../api/userAccess";
import PageLoading from "../../components/PageLoading";
import BidEmployeeFilterForm from "../../components/bid/BidEmployeeFilterForm";
import BidInfoBanner from "../../components/bid/BidInfoBanner";
import MyBidResultsDialog from "../../components/bid/MyBidResultsDialog";
import MyBidsFilterDialog from "../../components/bid/MyBidsFilterDialog";
import MyBidsTable from "../../components/bid/MyBidsTable";
import MyTimeOffBidResultsDialog from "../../components/bid/MyTimeOffBidResultsDialog";
import StarsBreadcrumbs from "../../components/fundamentals/StarsBreadcrumbs";
import {
  selectEmployeeBidData,
  updateBidTypeFilters,
  updateDateTypeFilter,
  updateEndDateFilter,
  updateEndDateFilterErrors,
  updateMobileSortByValue,
  updateSearchString,
  updateSearchStringErrors,
  updateStartDateFilter,
  updateStartDateFilterErrors,
  updateStatusFilters,
} from "../../slices/employeeBid/employeeBidSlice";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../../slices/userSession/userSessionSlice";
import { selectWebSocketEventData } from "../../slices/webSocketEvent/webSocketEventSlice";
import { logout } from "../../util/authentication";
import { BID_TYPE, EMPLOYEE_BID_STATUS } from "../../util/bid/bidConstants";
import { validateBidEndDateFilter, validateBidStartDateFilter } from "../../util/bid/bidInputValidation";
import { getScrubbedBidChoiceEvents } from "../../util/bid/bidScrubber";
import {
  DEFAULT_EMPLOYEE_BID_DATE_TYPE,
  DEFAULT_EMPLOYEE_BID_END_DATE_FILTER,
  DEFAULT_EMPLOYEE_BID_START_DATE_FILTER,
  DEFAULT_EMPLOYEE_BID_STATUS_FILTERS,
  DEFAULT_EMPLOYEE_BID_TYPE_FILTERS,
  filterBids,
  filterBidsOnInfoFilter,
  filterDeletedBidChoiceEvents,
} from "../../util/bid/filterBids";
import { initializeSearchIndex, searchBids } from "../../util/bid/searchBids";
import { sortEmployeeBidsByFilter } from "../../util/bid/sortBids";
import { getEmployeeStation } from "../../util/commonUtils";
import { BREADCRUMBS_TITLE, ERROR_MAX_PAYLOAD_LIMIT_MSG, HOME_PATH } from "../../util/constants";
import {
  deserializeDateFromRedux,
  getISODateTimeString,
  serializeDateToRedux,
  set_time_to_end_of_day,
} from "../../util/dateTimeFormatter";
import { bidDataErrorCodeToErrorMessageMap } from "../../util/errorMapper";
import { mergeWebSocketEmpBidChoices } from "../../util/webSocketUtils";
import MyBidsPageMobile from "./MyBidsPageMobile";
import PropTypes from "prop-types";

const MyBidsPage = (props) => {
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const bidData = useSelector(selectEmployeeBidData);
  const webSocketEventData = useSelector(selectWebSocketEventData);

  const _placeHolderFunc = () => {
    "";
  }; // Place holder to pass to props

  const dispatch = useDispatch();
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorObject, setErrorObject] = useState({});
  const [employeeBidChoiceEvents, setEmployeeBidChoiceEvents] = useState([]);
  const [filteredEmployeeBidChoiceEvents, setFilteredEmployeeBidChoiceEvents] = useState([]);
  const [searchedEmployeeBidChoiceEvents, setSearchedEmployeeBidChoiceEvents] = useState([]);
  const [isSearch, setIsSearch] = useState(false);

  const dateTypeFilterRef = useRef("");
  const startDateFilterRef = useRef(null);
  const endDateFilterRef = useRef(null);
  const bidWebSocketDataRef = useRef({});
  const bidTableDesktopRef = useGridApiRef();
  const bidTableMobileRef = useGridApiRef();
  const isApiSubscribedRef = useRef(true);

  const mobileOriginalBidTypeFiltersRef = useRef([]);
  const mobileOriginalStatusFiltersRef = useRef(DEFAULT_EMPLOYEE_BID_STATUS_FILTERS);
  const mobileOriginalDateTypeFiltersRef = useRef(DEFAULT_EMPLOYEE_BID_DATE_TYPE);
  const mobileOriginalBidStartDateFilterRef = useRef(
    serializeDateToRedux(DEFAULT_EMPLOYEE_BID_START_DATE_FILTER(stationFilter))
  );

  const mobileOriginalBidEndDateFilterRef = useRef(
    serializeDateToRedux(DEFAULT_EMPLOYEE_BID_END_DATE_FILTER(stationFilter))
  );
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  const stationFilterRef = useRef("");
  const [bidInfoFilter, setBidInfoFilter] = useState("");
  const [bidInfoFilteredStationBids, setBidInfoFilteredStationBids] = useState([]);
  const isEmployeePageRef = useRef(true);
  const [modifiedFiltersCount, setModifiedFiltersCount] = useState();
  const [isMobileFilterDialogOpen, setIsMobileFilterDialogOpen] = useState();
  const [bidTypes, setBidTypes] = useState([]);

  // RESULTS dialog
  const [selectedAwardedBidChoiceData, setSelectedAwardedBidChoiceData] = useState(null);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      stationFilterRef.current = stationFilter;
      const station = getEmployeeStation(props.featureConfig.userStations, userSession, stationFilter);
      dispatch(updateStationFilter(station));
      if (bidData.startDateFilter === null) {
        initializeFilterState();
      }
      if (bidData?.startDateFilterErrors === "" && bidData?.endDateFilterErrors === "") {
        updateEmployeeBidChoiceEventList(
          userSession.eid,
          bidData.dateTypeFilter,
          getStartDateFilterSafely(),
          getEndDateFilterSafely()
        );
      } else {
        setIsLoading(false);
      }
    }
    return () => {
      // Cleaning up lingering Promise handling...
      isApiSubscribedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  useEffect(() => {
    bidWebSocketDataRef.current = webSocketEventData.bidEmpEvents;
    if (Object.values(webSocketEventData.bidEmpEvents).length > 0) {
      if (!isFirstLoad) {
        setEmployeeBidChoiceEvents((prevState) => {
          const mergedEmployeeBidChoices = mergeWebSocketEmpBidChoices(webSocketEventData.bidEmpEvents, prevState);
          return filterDeletedBidChoiceEvents(mergedEmployeeBidChoices);
        });
        bidWebSocketDataRef.current = {};
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.bidEmpEvents]);

  useEffect(() => {
    let newFilteredBids = filterBids(
      employeeBidChoiceEvents,
      bidData.dateTypeFilter,
      getStartDateFilterSafely(),
      getEndDateFilterSafely(),
      bidData.bidTypeFilters,
      bidData.statusFilters,
      null,
      isEmployeePageRef.current
    );
    newFilteredBids = sortEmployeeBidsByFilter(bidData.mobileSortByValue, newFilteredBids);
    applyFilters(newFilteredBids);
    if (bidData.searchString !== "") {
      handleSearchSubmit(bidData.searchString);
    }
    setModifiedFiltersCount(getModifiedFiltersCount());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeBidChoiceEvents]);

  useEffect(() => {
    getBidTypeFeaturesByStation(stationFilter)
      .then((res) => res.json())
      .then((result) => {
        if (result.message) {
          console.error(result.message);
        } else if (result.bid_types) {
          setBidTypes(result.bid_types);
          if (isFirstLoad && bidData.bidTypeFilters.length === 0) dispatch(updateBidTypeFilters(result.bid_types));
        }
      })
      .catch((err) => {
        console.log("err", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationFilter]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const handleBidInfoFilterChange = (infoFilter) => {
    if (infoFilter === bidInfoFilter) {
      infoFilter = "";
    }
    setBidInfoFilter(infoFilter);
    setLoading(true);
    applyFilters(filteredEmployeeBidChoiceEvents, infoFilter);
    setLoading(false);
    applyBidTableScroll();
  };

  const doesResponseQueryParamsMatchCurrentFilters = (responseQueryParams) => {
    return (
      responseQueryParams.eid === userSession.eid &&
      decodeURI(responseQueryParams.dateType) === dateTypeFilterRef.current &&
      (responseQueryParams.startDate === startDateFilterRef.current.toMillis().toString() ||
        responseQueryParams.startDate === getISODateTimeString(startDateFilterRef.current)) &&
      (responseQueryParams.endDate === endDateFilterRef.current.toMillis().toString() ||
        responseQueryParams.endDate === getISODateTimeString(endDateFilterRef.current))
    );
  };

  const handleClickMobileFilterDialogOpen = () => {
    mobileOriginalBidTypeFiltersRef.current = bidData.bidTypeFilters;
    mobileOriginalStatusFiltersRef.current = bidData.statusFilters;
    mobileOriginalDateTypeFiltersRef.current = bidData.dateTypeFilter;
    mobileOriginalBidStartDateFilterRef.current = bidData.startDateFilter;
    mobileOriginalBidEndDateFilterRef.current = bidData.endDateFilter;
    setIsMobileFilterDialogOpen(true);
  };

  const revertMobileFilters = () => {
    setLoading(true);
    let newFilteredBids = filterBids(
      employeeBidChoiceEvents,
      mobileOriginalDateTypeFiltersRef.current,
      deserializeDateFromRedux(mobileOriginalBidStartDateFilterRef.current),
      deserializeDateFromRedux(mobileOriginalBidEndDateFilterRef.current),
      mobileOriginalBidTypeFiltersRef.current,
      mobileOriginalStatusFiltersRef.current,
      null,
      isEmployeePageRef.current
    );
    dispatch(updateBidTypeFilters(mobileOriginalBidTypeFiltersRef.current));
    dispatch(updateStatusFilters(mobileOriginalStatusFiltersRef.current));
    dispatch(updateDateTypeFilter(mobileOriginalDateTypeFiltersRef.current));
    dispatch(updateStartDateFilter(mobileOriginalBidStartDateFilterRef.current));
    dispatch(updateEndDateFilter(mobileOriginalBidEndDateFilterRef.current));
    dispatch(updateStartDateFilterErrors(""));
    dispatch(updateEndDateFilterErrors(""));
    sortMobileBids(bidData.mobileSortByValue, newFilteredBids);
    setLoading(false);
    applyBidTableScroll();
  };

  const handleClickMobileFilterDialogClose = () => {
    revertMobileFilters();
    setIsMobileFilterDialogOpen(false);
  };

  const getModifiedFiltersCount = () => {
    let count = 0;
    if (bidData.statusFilters.length !== DEFAULT_EMPLOYEE_BID_STATUS_FILTERS.length) count++;
    if (bidData.bidTypeFilters.length !== DEFAULT_EMPLOYEE_BID_TYPE_FILTERS.length) count++;
    if (bidData.dateTypeFilter !== DEFAULT_EMPLOYEE_BID_DATE_TYPE) count++;
    else if (
      bidData.dateTypeFilter === DEFAULT_EMPLOYEE_BID_DATE_TYPE &&
      (bidData.startDateFilter !== serializeDateToRedux(DEFAULT_EMPLOYEE_BID_START_DATE_FILTER(stationFilter)) ||
        bidData.endDateFilter !== serializeDateToRedux(DEFAULT_EMPLOYEE_BID_END_DATE_FILTER(stationFilter)))
    ) {
      count++;
    }
    return count;
  };

  const handleClickMobileFilterDialogApplyFiltersButton = () => {
    if (bidData.bidTypeFilters.length > 0 && bidData.statusFilters.length > 0) {
      if (
        mobileOriginalStatusFiltersRef.current === bidData.statusFilters &&
        mobileOriginalBidTypeFiltersRef.current === bidData.bidTypeFilters &&
        mobileOriginalDateTypeFiltersRef.current === bidData.dateTypeFilter &&
        mobileOriginalBidStartDateFilterRef.current === bidData.startDateFilter &&
        mobileOriginalBidEndDateFilterRef.current === bidData.endDateFilter
      ) {
        setIsMobileFilterDialogOpen(false);
        return;
      }
      updateEmployeeBidChoiceEventList(
        userSession.eid,
        bidData.dateTypeFilter,
        deserializeDateFromRedux(bidData.startDateFilter),
        deserializeDateFromRedux(bidData.endDateFilter)
      );
      setModifiedFiltersCount(getModifiedFiltersCount());
      sortMobileBids(bidData.mobileSortByValue, filteredEmployeeBidChoiceEvents);
      applyBidTableScroll();
      setIsMobileFilterDialogOpen(false);
    }
  };

  const updateEmployeeBidChoiceEventList = (eid, dateType, startDate, endDate) => {
    isApiSubscribedRef.current = true;
    setLoading(true);
    setIsError(false);
    setErrorObject({});
    dateTypeFilterRef.current = dateType;
    startDateFilterRef.current = startDate;
    endDateFilterRef.current = endDate;
    fetchLatestEmployeeBidChoiceEventsByEidAndDate(eid, dateType, startDate, endDate)
      .then((res) => res.json())
      .then((result) => {
        if (isApiSubscribedRef.current && doesResponseQueryParamsMatchCurrentFilters(result.queryParams)) {
          if (result.message) {
            if (result.message === ERROR_MAX_PAYLOAD_LIMIT_MSG) {
              setIsError(true);
              setErrorObject(bidDataErrorCodeToErrorMessageMap.bidDataMaxPayloadError);
            } else {
              throw new Error("Unexpected error");
            }
          } else {
            const scrubbedBidChoiceEvents = getScrubbedBidChoiceEvents(result.events);

            const mergedEmployeeBidChoices = mergeWebSocketEmpBidChoices(
              bidWebSocketDataRef.current,
              scrubbedBidChoiceEvents
            );

            setEmployeeBidChoiceEvents(filterDeletedBidChoiceEvents(mergedEmployeeBidChoices));

            bidWebSocketDataRef.current = {};
            setIsLoading(false);
            setIsFirstLoad(false);
          }
        }
      })
      .catch((err) => {
        console.error("error happened!", err);
        setIsLoading(false);
        setIsError(true);
        setErrorObject(bidDataErrorCodeToErrorMessageMap.unexpectedBidDataError);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const setLoading = (isLoading) => {
    if (!isFirstLoad) {
      setIsLoading(isLoading);
    }
  };

  const applyBidTableScroll = () => {
    // This ref scrolls desktop bids to top
    const currentDesktop = bidTableDesktopRef.current;
    if (currentDesktop !== null && currentDesktop.getRowModels && currentDesktop.getRowModels().size > 0) {
      currentDesktop.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
    }
  };

  const applySearch = (searchString, searchedBids) => {
    if (searchString === "") {
      setIsSearch(false);
    } else {
      setIsSearch(true);
    }
    setSearchedEmployeeBidChoiceEvents(searchedBids);
    applyBidTableScroll();
    setLoading(false);
  };

  const applyFilters = (filteredBids, infoFilter) => {
    if (infoFilter) {
      const infoFilteredBids = filterBidsOnInfoFilter(filteredBids, infoFilter);
      setBidInfoFilteredStationBids(infoFilteredBids);
      initializeSearchIndex(infoFilteredBids, isEmployeePageRef.current);
    } else {
      initializeSearchIndex(filteredBids, isEmployeePageRef.current);
      setBidInfoFilter("");
      setBidInfoFilteredStationBids([]);
    }

    if (isSearch) {
      searchBids(bidData.searchString, applySearch, isEmployeePageRef.current);
    }

    setFilteredEmployeeBidChoiceEvents(filteredBids);
  };

  const initializeFilterState = () => {
    dispatch(updateStartDateFilter(serializeDateToRedux(DEFAULT_EMPLOYEE_BID_START_DATE_FILTER(stationFilter))));
    dispatch(updateEndDateFilter(serializeDateToRedux(DEFAULT_EMPLOYEE_BID_END_DATE_FILTER(stationFilter))));
  };

  const getStartDateFilterSafely = () => {
    return bidData.startDateFilter
      ? deserializeDateFromRedux(bidData.startDateFilter)
      : DEFAULT_EMPLOYEE_BID_START_DATE_FILTER(stationFilter);
  };

  const getEndDateFilterSafely = () => {
    return bidData.endDateFilter
      ? deserializeDateFromRedux(bidData.endDateFilter)
      : DEFAULT_EMPLOYEE_BID_END_DATE_FILTER(stationFilter);
  };

  const isStatusSelectAllChecked = () => {
    return bidData.statusFilters.length === Object.values(EMPLOYEE_BID_STATUS).length;
  };

  const handleSearchChange = (event) => {
    dispatch(updateSearchString(event.target.value));
  };

  const handleSearchBlur = (event) => {
    if (event.target.value.trim() === "") {
      handleSearchSubmit("");
    }
  };

  const handleSearchEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit(bidData.searchString);
    }
  };

  const handleSearchCancel = () => {
    dispatch(updateSearchString(""));
    handleSearchSubmit("");
  };

  const handleSearchSubmit = (newSearchString) => {
    dispatch(updateSearchStringErrors(""));
    const newSearchStringTrimmed = newSearchString.trim();

    searchBids(newSearchStringTrimmed, applySearch, isEmployeePageRef.current);
  };

  const handleStartDateFilterChange = (date) => {
    let newStartDateFilterErrors = validateBidStartDateFilter(
      date,
      deserializeDateFromRedux(bidData.endDateFilter),
      stationFilter
    );
    dispatch(updateStartDateFilter(serializeDateToRedux(date)));
    dispatch(updateStartDateFilterErrors(newStartDateFilterErrors));
    if (newStartDateFilterErrors === "") {
      if (props.isDesktopView) {
        updateEmployeeBidChoiceEventList(
          userSession.eid,
          bidData.dateTypeFilter,
          date,
          deserializeDateFromRedux(bidData.endDateFilter)
        );
      }
      dispatch(updateEndDateFilterErrors(""));
      applyBidTableScroll();
    }
  };

  const handleEndDateFilterChange = (event) => {
    let date = set_time_to_end_of_day(event);
    let newEndDateFilterErrors = validateBidEndDateFilter(
      date,
      deserializeDateFromRedux(bidData.startDateFilter),
      stationFilter
    );
    dispatch(updateEndDateFilter(serializeDateToRedux(date)));
    dispatch(updateEndDateFilterErrors(newEndDateFilterErrors));
    if (newEndDateFilterErrors === "") {
      if (props.isDesktopView) {
        updateEmployeeBidChoiceEventList(
          userSession.eid,
          bidData.dateTypeFilter,
          deserializeDateFromRedux(bidData.startDateFilter),
          date
        );
      }
      dispatch(updateStartDateFilterErrors(""));
      applyBidTableScroll();
    }
  };

  const handleDateTypeFilterChange = (event) => {
    props.isDesktopView &&
      updateEmployeeBidChoiceEventList(
        userSession.eid,
        event.target.value,
        deserializeDateFromRedux(bidData.startDateFilter),
        deserializeDateFromRedux(bidData.endDateFilter)
      );

    dispatch(updateDateTypeFilter(event.target.value));
    applyBidTableScroll();
  };

  const handleStatusFilterChange = (event) => {
    let newStatusFilterSelectedList = event.target.value;
    if (newStatusFilterSelectedList.includes("Select All")) {
      if (isStatusSelectAllChecked()) {
        newStatusFilterSelectedList = [];
      } else {
        newStatusFilterSelectedList = Object.values(EMPLOYEE_BID_STATUS);
      }
    }

    setLoading(true);

    let newFilteredBids = filterBids(
      employeeBidChoiceEvents,
      bidData.dateTypeFilter,
      deserializeDateFromRedux(bidData.startDateFilter),
      deserializeDateFromRedux(bidData.endDateFilter),
      bidData.bidTypeFilters,
      newStatusFilterSelectedList,
      null,
      isEmployeePageRef.current
    );
    dispatch(updateStatusFilters(newStatusFilterSelectedList));
    applyFilters(newFilteredBids);
    setLoading(false);
    applyBidTableScroll();
  };

  const handleBidTypeFilterChange = (event) => {
    let newBidTypeFilterSelectedList = event.target.value;
    if (newBidTypeFilterSelectedList.includes("Select All")) {
      if (isBidTypeSelectAllChecked()) {
        newBidTypeFilterSelectedList = [];
      } else {
        newBidTypeFilterSelectedList = Object.values(bidTypes);
      }
    }
    setLoading(true);
    let newFilteredList = filterBids(
      employeeBidChoiceEvents,
      bidData.dateTypeFilter,
      deserializeDateFromRedux(bidData.startDateFilter),
      deserializeDateFromRedux(bidData.endDateFilter),
      newBidTypeFilterSelectedList,
      bidData.statusFilters,
      null,
      isEmployeePageRef.current
    );
    dispatch(updateBidTypeFilters(newBidTypeFilterSelectedList));
    applyFilters(newFilteredList);
    setLoading(false);
    applyBidTableScroll();
  };

  const isBidTypeSelectAllChecked = () => {
    return bidData.bidTypeFilters.length === Object.values(bidTypes).length;
  };

  const handleMobileSortByChange = (event) => {
    sortMobileBids(event.target.value, filteredEmployeeBidChoiceEvents);
    dispatch(updateMobileSortByValue(event.target.value));
    applyBidTableScroll();
  };

  const sortMobileBids = (sortValue, filteredEmployeeBidChoiceEvents) => {
    let sortedBidChoiceEvents = [...filteredEmployeeBidChoiceEvents];
    sortEmployeeBidsByFilter(sortValue, sortedBidChoiceEvents);
    applyFilters(sortedBidChoiceEvents);
    setFilteredEmployeeBidChoiceEvents(sortedBidChoiceEvents);
  };

  const handleMyBidResultsDialogClose = () => {
    setSelectedAwardedBidChoiceData(null);
  };

  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.MY_BIDS];
  const breadcrumbsLink = [HOME_PATH];
  const renderDesktopBidEmployeeFilterForm = () => {
    return (
      <>
        <StarsBreadcrumbs
          isConfirmationRequired={false}
          breadcrumbsLinks={breadcrumbsLink}
          breadcrumbsList={breadcrumbsList}
        />
        <BidEmployeeFilterForm
          title={"My Bids"}
          onDateTypeFilterChange={handleDateTypeFilterChange}
          onStatusFilterChange={handleStatusFilterChange}
          startDateFilter={getStartDateFilterSafely()}
          onStartDateFilterChange={handleStartDateFilterChange}
          endDateFilter={getEndDateFilterSafely()}
          onEndDateFilterChange={handleEndDateFilterChange}
          onBidTypeFilterChange={handleBidTypeFilterChange}
          onSearchChange={handleSearchChange}
          onSearchBlur={handleSearchBlur}
          onSearchEnterKeyPressed={handleSearchEnterKeyPressed}
          onSearchCancel={handleSearchCancel}
          onSearchSubmit={handleSearchSubmit}
          dateTypeFilter={bidData.dateTypeFilter}
          startDateFilterErrors={bidData.startDateFilterErrors}
          endDateFilterErrors={bidData.endDateFilterErrors}
          bidTypeFilters={bidData.bidTypeFilters}
          allBidTypesFilters={Object.values(bidTypes)}
          bidStatusFilters={bidData.statusFilters}
          searchString={bidData.searchString}
          searchStringErrors={bidData.searchStringErrors}
          stationFilter={getEmployeeStation(props.featureConfig.userStations, userSession, stationFilter)}
        />
        <div>
          <BidInfoBanner
            bids={filteredEmployeeBidChoiceEvents}
            handleInfoFilter={handleBidInfoFilterChange}
            bidInfoFilter={bidInfoFilter}
          />
          {renderMyBidsTable()}
        </div>
      </>
    );
  };

  const renderMobileBidEmployeeFilterForm = () => {
    return (
      <div data-testid="employee-my-bids-mobile-page">
        <MyBidsPageMobile
          bidList={
            isSearch
              ? searchedEmployeeBidChoiceEvents
              : bidInfoFilter
              ? bidInfoFilteredStationBids
              : filteredEmployeeBidChoiceEvents
          }
          featureConfig={props.featureConfig}
          isLoading={isLoading}
          onSearchChange={handleSearchChange}
          onSearchBlur={handleSearchBlur}
          onSearchEnterKeyPressed={handleSearchEnterKeyPressed}
          onSearchCancel={handleSearchCancel}
          onSearchSubmit={handleSearchSubmit}
          searchString={bidData.searchString}
          searchStringErrors={bidData.searchStringErrors}
          mobileSortByValue={bidData.mobileSortByValue}
          handleMobileSortByChange={handleMobileSortByChange}
          bidTableMobileRef={bidTableMobileRef}
          onClickMobileFilterDialogOpen={handleClickMobileFilterDialogOpen}
          modifiedFiltersCount={modifiedFiltersCount}
          setSelectedAwardedBidChoiceData={setSelectedAwardedBidChoiceData}
        />
        {
          <MyBidsFilterDialog
            isMobileFilterDialogOpen={isMobileFilterDialogOpen}
            onDateTypeFilterChange={handleDateTypeFilterChange}
            onStatusFilterChange={handleStatusFilterChange}
            startDateFilter={getStartDateFilterSafely()}
            onStartDateFilterChange={handleStartDateFilterChange}
            endDateFilter={getEndDateFilterSafely()}
            onEndDateFilterChange={handleEndDateFilterChange}
            onBidTypeFilterChange={handleBidTypeFilterChange}
            dateTypeFilter={bidData.dateTypeFilter}
            startDateFilterErrors={bidData.startDateFilterErrors}
            endDateFilterErrors={bidData.endDateFilterErrors}
            bidTypeFilters={bidData.bidTypeFilters}
            allBidTypesFilters={Object.values(bidTypes)}
            bidStatusFilters={bidData.statusFilters}
            stationFilter={getEmployeeStation(props.featureConfig.userStations, userSession, stationFilter)}
            onMobileFilterDialogClose={handleClickMobileFilterDialogClose}
            onClickMobileFilterDialogApplyFiltersButton={handleClickMobileFilterDialogApplyFiltersButton}
          />
        }
      </div>
    );
  };

  const renderMyBidsTable = () => {
    return (
      <MyBidsTable
        bidList={
          isSearch
            ? searchedEmployeeBidChoiceEvents
            : bidInfoFilter
            ? bidInfoFilteredStationBids
            : filteredEmployeeBidChoiceEvents
        }
        isDesktopView={props.isDesktopView}
        featureConfig={props.featureConfig}
        isAdminPage={false}
        isFirstLoad={isFirstLoad}
        isLoading={isLoading}
        isError={isError}
        errorObject={errorObject}
        handleCancelClick={_placeHolderFunc}
        handleRetractClick={_placeHolderFunc}
        station={stationFilter}
        tableDesktopRef={bidTableDesktopRef}
        bidTableMobileRef={bidTableMobileRef}
        setSelectedAwardedBidChoiceData={setSelectedAwardedBidChoiceData}
      />
    );
  };

  return (
    <div className="App-contents" data-testid="employee-my-bids-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>
          {props.isDesktopView ? renderDesktopBidEmployeeFilterForm() : renderMobileBidEmployeeFilterForm()}
          {selectedAwardedBidChoiceData &&
            (selectedAwardedBidChoiceData.bidType === BID_TYPE.SHIFT ? (
              <MyBidResultsDialog
                selectedAwardedBidChoiceData={selectedAwardedBidChoiceData}
                onClickDialogClose={handleMyBidResultsDialogClose}
              />
            ) : (
              <MyTimeOffBidResultsDialog
                selectedAwardedBidChoiceData={selectedAwardedBidChoiceData}
                onClickDialogClose={handleMyBidResultsDialogClose}
              />
            ))}
        </>
      )}
    </div>
  );
};

MyBidsPage.propTypes = {
  isDesktopView: PropTypes.bool.isRequired,
  featureConfig: PropTypes.object.isRequired,
};

export default MyBidsPage;
